import { createErrorBuilder, BaseError } from '@wix/public-editor-platform-errors';
import { PlatformAppEvent } from '@wix/public-editor-platform-events';

var EditorPlatformContextErrorCode = /* @__PURE__ */ ((EditorPlatformContextErrorCode2) => {
  EditorPlatformContextErrorCode2["IncorrectEnvironment"] = "IncorrectEnvironment";
  return EditorPlatformContextErrorCode2;
})(EditorPlatformContextErrorCode || {});
class EditorPlatformContextError extends BaseError {
  state = {};
  constructor(message, code) {
    super(message, code, "EP Context Error");
  }
  withUrl(url) {
    this.state = { ...this.state, url };
    return this;
  }
  withAppDefinitionId(appDefinitionId) {
    this.state = { ...this.state, appDefinitionId };
    return this;
  }
}
const createEditorPlatformContextError = createErrorBuilder(EditorPlatformContextError);

async function transformEventPayload(eventPayload, privateAPI) {
  if (!eventPayload?.type) {
    return eventPayload;
  }
  switch (eventPayload.type) {
    case "componentSelectionChanged":
      const componentRefs = eventPayload.componentRefs || [];
      const components = await Promise.all(
        componentRefs.map((ref) => {
          return privateAPI.components.getComponent(ref);
        })
      );
      return {
        type: eventPayload.type,
        components
      };
    default:
      return eventPayload;
  }
}

class ApplicationBoundEvents {
  constructor(appDefinitionId, events, privateAPI) {
    this.appDefinitionId = appDefinitionId;
    this.privateAPI = privateAPI;
    this.events = events;
    this.subscribe = events.subscribe.bind(events);
    this.commit = events.commit.bind(events);
    this.startTransaction = events.startTransaction.bind(events);
    this.silent = events.silent.bind(events);
  }
  events;
  subscribe;
  commit;
  startTransaction;
  silent;
  notify(event) {
    this.events.notify({
      type: event.type,
      payload: event.payload,
      meta: {
        appDefinitionId: this.appDefinitionId
      }
    });
  }
  notifyCustomEvent(type, payload) {
    this.notify({
      type: PlatformAppEvent.CustomEvent,
      payload: {
        ...payload,
        type
      }
    });
  }
  /**
   * TODO: we should use same interface for all events
   * (subscribe vs addEventListener)
   */
  addEventListener(eventType, fn) {
    return this.events.subscribe(async (event) => {
      const isAppMatch = event.meta?.appDefinitionId === this.appDefinitionId || event.meta?.appDefinitionId === null;
      const transformPayload = () => transformEventPayload(event.payload, this.privateAPI);
      if (eventType === "*") {
        fn(await transformPayload());
      } else if (event.type === PlatformAppEvent.CustomEvent) {
        if (eventType === event.payload?.type && !isAppMatch) {
          fn(await transformPayload());
        }
      } else if (event.type === PlatformAppEvent.HostEvent) {
        if (eventType === event.payload?.type && isAppMatch) {
          fn(await transformPayload());
        }
      }
    });
  }
}

const WAIT_INJECTED_TIMEOUT = 200;
const WAIT_INJECTED_RETRY_COUNT = 50;
class ContextInjectionStatus {
  injectionUUID;
  injectionUUID_Deprecated;
  constructor(uuid) {
    this.injectionUUID_Deprecated = `__${uuid}_CONTEXT_INJECTION_STATUS_KEY`;
    this.injectionUUID = `__${uuid}_CONTEXT_INJECTION_STATUS_KEY#0.1`;
    const promise = this.#getGlobalInjectionPromise();
    if (!promise) {
      this.#createGlobalInjectionPromise();
    }
  }
  #getGlobalInjectionPromise() {
    const ref = globalThis[this.injectionUUID];
    return ref?.promise;
  }
  #createGlobalInjectionPromise() {
    let _resolve;
    const promise = new Promise((resolve) => {
      _resolve = resolve;
    });
    globalThis[this.injectionUUID] = {
      promise,
      resolve: _resolve
    };
    globalThis[this.injectionUUID_Deprecated] = new Promise((resolve) => {
      promise.then(resolve);
    });
  }
  resolveInjected() {
    globalThis[this.injectionUUID]?.resolve();
  }
  waitInjected() {
    return new Promise((resolve, reject) => {
      let injected = false;
      let timeoutId;
      let retryCount = 0;
      const timeout = () => {
        if (injected) {
          return;
        }
        timeoutId = setTimeout(() => {
          retryCount++;
          if (retryCount < WAIT_INJECTED_RETRY_COUNT) {
            if (retryCount % 10 === 0) {
              console.log(
                createEditorPlatformContextError(
                  EditorPlatformContextErrorCode.IncorrectEnvironment,
                  "contexts are not resolved, still re-trying"
                ).withMessage(`try number ${retryCount}`).message
              );
            }
            timeout();
            return;
          }
          if (!injected) {
            const error = createEditorPlatformContextError(
              EditorPlatformContextErrorCode.IncorrectEnvironment,
              "contexts are not resolved, threw by timeout"
            );
            reject(error);
          }
        }, WAIT_INJECTED_TIMEOUT);
      };
      timeout();
      this.#getGlobalInjectionPromise()?.then(() => {
        injected = true;
        clearTimeout(timeoutId);
        resolve();
      });
    });
  }
}

const ENVIRONMENT_CONTEXT_KEY = "__ENVIRONMENT_CONTEXT_KEY";
var PlatformEnvironment = /* @__PURE__ */ ((PlatformEnvironment2) => {
  PlatformEnvironment2["Worker"] = "Worker";
  PlatformEnvironment2["Frame"] = "Frame";
  PlatformEnvironment2["ComponentPanel"] = "ComponentPanel";
  return PlatformEnvironment2;
})(PlatformEnvironment || {});
class EnvironmentContext {
  constructor(environmentContext) {
    this.environmentContext = environmentContext;
  }
  static status = new ContextInjectionStatus("environment");
  static async inject(context) {
    if (globalThis[ENVIRONMENT_CONTEXT_KEY]) {
      throw createEditorPlatformContextError(
        EditorPlatformContextErrorCode.IncorrectEnvironment,
        "Environment context already exists and should not be overridden"
      );
    }
    globalThis[ENVIRONMENT_CONTEXT_KEY] = new EnvironmentContext(context);
    this.status.resolveInjected();
  }
  static async getInstance() {
    await this.status.waitInjected();
    return globalThis[ENVIRONMENT_CONTEXT_KEY];
  }
  getPrivateAPI() {
    return this.environmentContext.privateApi;
  }
  getEvents() {
    return this.environmentContext.events;
  }
  getApplicationAPIs() {
    return this.environmentContext.applicationAPIs ?? {};
  }
  getEnvironment() {
    return this.environmentContext.environment;
  }
}

const APPLICATION_CONTEXT_KEY = "__APPLICATION_CONTEXT_KEY";
class ApplicationContext {
  constructor(context, environment) {
    this.context = context;
    this.environment = environment;
    this.events = new ApplicationBoundEvents(
      context.appDefinitionId ? context.appDefinitionId : null,
      this.environment.getEvents(),
      this.environment.getPrivateAPI()
    );
  }
  static status = new ContextInjectionStatus("application");
  /**
   * TODO: use generics for context type
   * - application
   * - editor
   */
  static async inject(context) {
    if (globalThis[APPLICATION_CONTEXT_KEY]) {
      throw createEditorPlatformContextError(
        EditorPlatformContextErrorCode.IncorrectEnvironment,
        "Application context already exists and should not be overridden"
      );
    }
    globalThis[APPLICATION_CONTEXT_KEY] = new ApplicationContext(
      context,
      await EnvironmentContext.getInstance()
    );
    this.status.resolveInjected();
  }
  static async getInstance() {
    if (typeof __APPLICATION_CONTEXT_KEY !== "undefined") {
      return __APPLICATION_CONTEXT_KEY;
    }
    await this.status.waitInjected();
    return globalThis[APPLICATION_CONTEXT_KEY];
  }
  events;
  getContext() {
    return this.context;
  }
  getAppDefinitionId() {
    return this.context.appDefinitionId;
  }
  getManifestContextId() {
    return this.context.manifestContextId;
  }
  getEvents() {
    return this.events;
  }
  getEnvironmentContext() {
    return this.environment;
  }
  getPrivateAPI() {
    return this.environment.getPrivateAPI();
  }
  getPrivateApplicationAPI() {
    const appDefinitionId = this.getAppDefinitionId();
    if (!appDefinitionId) {
      throw createEditorPlatformContextError(
        EditorPlatformContextErrorCode.IncorrectEnvironment,
        "appDefinitionId is not available"
      );
    }
    return this.environment.getApplicationAPIs()[appDefinitionId];
  }
}

export { APPLICATION_CONTEXT_KEY, ApplicationBoundEvents, ApplicationContext, ENVIRONMENT_CONTEXT_KEY, EnvironmentContext, PlatformEnvironment };
//# sourceMappingURL=index.js.map
